/**
 * ConversationContactNotesModal
 * -----------------------------------------------------------------------------
 * Modal de listagem de anotações de um contato (aberto a partir do FAB do chat).
 * - Botão "Nova" abre o editor em modo criação.
 * - Clique em linha abre o editor em modo edição/visualização.
 *
 * @version 1.0.0
 * RELEASE: 2026-06-03 v1.0.0 - Criação inicial.
 */
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, RefreshCw, StickyNote } from 'lucide-react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { logger } from '@/lib/logger';

import { ContactNotesTable, ContactNoteRow } from './ContactNotesTable';
import { ContactNoteEditorDialog } from './ContactNoteEditorDialog';

export interface ConversationContactNotesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contactId: string;
}

export function ConversationContactNotesModal({
  open,
  onOpenChange,
  contactId,
}: ConversationContactNotesModalProps) {
  const qc = useQueryClient();
  const { currentTenant, user } = useAuth();
  const { isAdmin } = usePermissions();

  const [editing, setEditing] = useState<ContactNoteRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<ContactNoteRow | null>(
    null,
  );
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Listagem
  // ---------------------------------------------------------------------------
  const { data, isFetching, refetch } = useQuery({
    queryKey: ['contact-notes', contactId, currentTenant?.id],
    enabled: open && !!contactId && !!currentTenant?.id,
    staleTime: 10_000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('list_contact_notes', {
        p_contact_id: contactId,
        p_tenant_hint: currentTenant?.id ?? null,
      } as never);
      if (error) throw error;
      return (data ?? []) as ContactNoteRow[];
    },
  });

  // Realtime: invalida lista em mudanças relevantes.
  useEffect(() => {
    if (!open || !contactId) return;
    const channel = supabase
      .channel(`contact-notes-${contactId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'contact_notes',
          filter: `contact_id=eq.${contactId}`,
        },
        () => {
          qc.invalidateQueries({ queryKey: ['contact-notes', contactId] });
          qc.invalidateQueries({
            queryKey: ['contact-notes-count', contactId],
          });
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [open, contactId, qc]);

  const refreshAll = () => {
    qc.invalidateQueries({ queryKey: ['contact-notes', contactId] });
    qc.invalidateQueries({ queryKey: ['contact-notes-count', contactId] });
  };

  // ---------------------------------------------------------------------------
  // Exclusão
  // ---------------------------------------------------------------------------
  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeletingId(confirmDelete.id);
    try {
      const { error } = await supabase.rpc('delete_contact_note', {
        p_note_id: confirmDelete.id,
        p_tenant_hint: currentTenant?.id ?? null,
      } as never);
      if (error) throw error;
      toast.success('Anotação excluída');
      setConfirmDelete(null);
      refreshAll();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao excluir';
      logger.error('ContactNotesModal', `delete: ${String(err)}`);
      toast.error(msg);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-5xl w-[95vw] h-[90dvh] md:h-auto md:max-h-[85dvh] flex flex-col p-0 gap-0 overflow-hidden">
          {/* Capa premium padronizada (mesmo padrão do editor/agendamento) */}
          <DialogHeader className="px-4 sm:px-6 py-3 sm:py-4 border-b bg-gradient-to-r from-primary/5 via-primary/3 to-transparent">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3 min-w-0 pr-8">
                <div className="p-1.5 sm:p-2 bg-primary/10 rounded-lg shrink-0">
                  <StickyNote className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                </div>
                <div className="flex flex-col min-w-0">
                  <DialogTitle className="truncate">Anotações do contato</DialogTitle>
                  <DialogDescription className="text-xs line-clamp-2 sm:line-clamp-none">
                    Histórico de anotações registradas para este contato. Clique para visualizar, editar ou reabrir.
                  </DialogDescription>
                </div>
              </div>
              <div className="flex items-center gap-2 justify-end w-full sm:w-auto sm:pr-8">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetch()}
                  disabled={isFetching}
                  className="h-7 px-2 sm:px-3 gap-2"
                  aria-label="Atualizar"
                >
                  <RefreshCw
                    className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`}
                  />
                  <span className="hidden sm:inline">Atualizar</span>
                </Button>
                <Button
                  size="sm"
                  onClick={() => setCreating(true)}
                  className="h-7 px-2 sm:px-3 gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Nova
                </Button>
              </div>
            </div>
          </DialogHeader>


          <div className="flex-1 overflow-auto px-6 py-4">
            {isFetching && !data ? (
              <div className="flex items-center justify-center py-16 text-muted-foreground gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Carregando...
              </div>
            ) : !data || data.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center text-muted-foreground">
                <StickyNote className="h-10 w-10 mb-3 opacity-40" />
                <p className="text-sm">
                  Nenhuma anotação para este contato ainda.
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-3 gap-2"
                  onClick={() => setCreating(true)}
                >
                  <Plus className="h-4 w-4" /> Criar primeira anotação
                </Button>
              </div>
            ) : (
              <ContactNotesTable
                items={data}
                currentUserId={user?.id ?? null}
                isAdmin={isAdmin}
                deletingId={deletingId}
                onRowClick={(row) => setEditing(row)}
                onDelete={(row) => setConfirmDelete(row)}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Editor: modo edição */}
      {editing && (
        <ContactNoteEditorDialog
          open={!!editing}
          onOpenChange={(v) => !v && (setEditing(null), refreshAll())}
          noteId={editing.id}
          contactId={contactId}
          readOnly={
            !(isAdmin || editing.created_by === (user?.id ?? null))
          }
          onSaved={() => refreshAll()}
        />
      )}

      {/* Editor: modo criação */}
      {creating && (
        <ContactNoteEditorDialog
          open={creating}
          onOpenChange={(v) => {
            setCreating(v);
            if (!v) refreshAll();
          }}
          noteId={null}
          contactId={contactId}
          onSaved={() => refreshAll()}
        />
      )}

      {/* Confirmação de exclusão */}
      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir anotação?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação removerá a anotação e todos os anexos vinculados. Não
              poderá ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
